import { useMemo } from 'react';
import { useAuth } from './AuthContext';

/**
 * The client-side mirror of services/document_visibility_service.py's
 * CAPABILITY_RESOURCE map (GitHub issue #138).
 *
 * Keyed by CAPABILITY, never by resource, for the same reason the backend is:
 * splitting `document_extras` into per-panel resources should be an edit to
 * this one object and nothing else. Issue #211 did exactly that -- three ways,
 * and no consumer of this hook changed. Keep the keys identical to the
 * backend's so the two can be diffed by eye.
 *
 * A capability MISSING from this object falls back to `can(undefined, 'read')`,
 * which is silently always false. So an addition on the backend that is not
 * mirrored here does not fail loudly; it hides a panel from everyone whose
 * `view_rights` has not loaded yet.
 */
const CAPABILITY_RESOURCE = {
  drafts: 'document_drafts',
  // `document_extras` kept its wire value through issue #211's split even
  // though it now means version history alone -- renaming it would have meant
  // rewriting live grants for a string no administrator ever sees.
  versions: 'document_extras',
  comments: 'document_comments',
  type_check: 'document_ai_panels',
  similar_documents: 'document_ai_panels',
  reuse_opportunities: 'document_ai_panels',
  // Issue #68's template conformance -- a corpus-scan-shaped panel result
  // like similar_documents/reuse_opportunities above, not an authoring
  // surface, so it follows their precedent rather than staying unmapped the
  // way policy_guide_draft/regulatory_conformance do.
  template_conformance: 'document_ai_panels',
};

/**
 * useDocumentViewRights(document).can('versions')
 *
 * PASS THE DOCUMENT. `GET /api/documents/{id}` now returns a `view_rights` map
 * keyed by the same capabilities as CAPABILITY_RESOURCE above, computed
 * server-side for THAT document (GitHub issue #181). When it is present it is
 * authoritative in the only sense that matters here: it is the same
 * document_visibility_service.may_see() the endpoints themselves call, so the
 * exemptions that make this feature usable -- an approver on an open gate, an
 * editor, the owner, a named contributor -- are finally reflected on screen.
 *
 * That closes the gap user-requirements.md 26 recorded, and which the previous
 * version of this docstring described as its honest failure mode: both
 * resources are hierarchical, so /api/auth/me computes them with
 * any_scope=True, meaning "somewhere" rather than "on the document you are
 * looking at" -- and none of the exemptions are modelled client-side at all. A
 * panel could therefore stay hidden from somebody the server would have let
 * through.
 *
 * WITHOUT A DOCUMENT IT FALLS BACK TO THAT OLD HINT, deliberately. Not every
 * caller has one loaded (RevisionViewPage renders panels off a revision), and
 * a fetch still in flight has none yet. The fallback is the previous behaviour
 * exactly, so nothing regresses while a document is loading.
 *
 * STILL AN AFFORDANCE EITHER WAY. The server re-checks on every request and is
 * the only control.
 */
export function useDocumentViewRights(document) {
  const { can } = useAuth();
  const viewRights = document?.view_rights;
  // MEMOISED, and it has to be: DocumentViewPage puts this object in a
  // useCallback dependency array, so returning a fresh one every render would
  // rebuild fetchAll every render and re-fetch the document forever. That is
  // also why the dependency is `viewRights` -- the map itself, a stable
  // reference for as long as the document object is -- and not `document`.
  return useMemo(() => ({
    can: (capability) => {
      const granted = viewRights?.[capability];
      if (granted !== undefined && granted !== null) return granted;
      return can(CAPABILITY_RESOURCE[capability], 'read');
    },
  }), [can, viewRights]);
}

export default useDocumentViewRights;
